import {FormattedMessage, useIntl} from "react-intl";
import '../scss/Users.scss';
import React, {useEffect, useMemo, useRef, useState} from "react";
import {GetRoleTranslationWithInstanceLang, GetSupportRoles, GetUsers} from "../api/APISurvey";
import {
    GetSMSCountryCode, GetUserMeta,
    PostAnnieUser,
    PostUserUploadSave,
    PostUserUploadValidate
} from "../api/APIContact";
import {
    useColumnOrder,
    useFilters,
    useFlexLayout,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
    useResizeColumns
} from "react-table";
import {GlobalFilter} from "./SurveyRecipients";
import {ReactComponent as First} from "../svg/first.svg";
import {ReactComponent as Previous} from "../svg/previous.svg";
import {ReactComponent as Next} from "../svg/next.svg";
import {ReactComponent as Last} from "../svg/last.svg";
import {ReactComponent as SaveIcon} from "../svg/save.svg";
import {
    ColumnEditor, DataTable,
    FileCheckSkeleton,
    FileDisplayAndUpload,
    FileInfo,
    FiltersDisplay,
    StudentTable
} from "./Contacts";
import _ from "lodash"
import {ReactComponent as UploadIcon} from "../svg/upload.svg";
import {useSnackbar} from "../Content";
import {Popover, Popup, SlatSkeleton, TagInput} from "../UIElements";
import {ReactComponent as Alert} from "../svg/alert.svg";
import {ReactComponent as LoadingIcon} from "../svg/loading.svg";
import {Slat} from "./SurveySummary";
import {ReactComponent as OptionsIcon} from "../svg/options.svg";
import {ReactComponent as DeleteIcon} from "../svg/delete-s.svg";
import {formatUserDataForPost, validUntilDate} from "../Formats";
import {ReactComponent as LoaderIcon} from "../svg/loader.svg";
import {useLanguageData} from "../App";
import { Users, Plus } from 'lucide-react';
import {Link} from "react-router-dom";

export default function UsersView() {
    const intl = useIntl()
    const getUsers = GetUsers()
    const getUserMeta = GetUserMeta()
    const getSupportRoles = GetSupportRoles()

    useEffect(()=>{
        document.title = intl.formatMessage({
            id:"main.sidebar.users",
            defaultMessage:"Users",
        }) + " | Annie Admin"
    },[])

    return (
        <div className={"content"}>
            <div className={"header-container"}>
                <h1>
                    <Users size={24} strokeWidth={2} />
                    {intl.formatMessage(
                    {
                        id: 'main.sidebar.users',
                        defaultMessage: 'Users',
                    }
                )}
                </h1>
            </div>
            <UploadUsers refetchUsers={()=>getUsers.refetch()}/>
            <UsersRender getUsers={getUsers} getUserMeta={getUserMeta} getSupportRoles={getSupportRoles}/>
        </div>
    );
}

function UploadUsers({refetchUsers}) {
    const intl = useIntl()
    const fileInput = React.useRef(null)
    const dropZone = React.useRef(null)
    const [uploadFiles, setUploadFiles] = useState([])

    const handleUserUpload = e => {
        setUploadFiles(e.target.files)
    }

    const triggerFileInput = () => {
        fileInput.current.click()
    }

    const emptyFileInput = () => {
        setUploadFiles([])
        fileInput.current.value = null
    }

    const reTriggerFileInput = () => {
        fileInput.current.value = null
        fileInput.current.click()
    }

    const [dragOver, setDragOver] = useState(false)

    const handleDrop = e => {
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.items) {
            if (e.dataTransfer.items[0].kind === 'file') {
                const file = e.dataTransfer.items[0].getAsFile()
                if (file) {
                    setUploadFiles([e.dataTransfer.items[0].getAsFile()])
                }
            }
        } else {
            setUploadFiles(e.dataTransfer.files)
        }
    }

    const handleDragOver = e => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDragEnter = e => {
        e.preventDefault()
        setDragOver(true)
    }

    const handleDragLeave = e => {
        e.preventDefault()
        setDragOver(false)
    }
    return <>
        <div className={dragOver ? "drop-zone drag-over" : "drop-zone"} ref={dropZone} onDrop={e=>handleDrop(e)} onDragOver={e=>handleDragOver(e)} onDragEnter={e=>handleDragEnter(e)} onDragLeave={e=>handleDragLeave(e)}>
            <UploadIcon />
            <p>
                <FormattedMessage
                    id={"contact.upload.dropOrChooseFile"}
                    defaultMessage={"<b>Drop or choose</b> a .xlsx file"}
                    values={{b: (...text) => <b>{text}</b>}}/>
            </p>
            <button aria-hidden={true} className={"main-button"} onClick={()=>triggerFileInput()}>
                <UploadIcon />
                {intl.formatMessage(
                    {
                        id: 'users.uploadUsers',
                        defaultMessage: 'Upload users',
                    }
                )}
            </button>
            <a className={"download-template"} href={"/admin/downloads/annieuser.xlsx"} download={true}>{intl.formatMessage({
                id:'contact.downloadTemplate',
                defaultMessage:"Download template"
            })}</a>
        </div>

        <label htmlFor={"contact-upload"} className={"hidden-element"}>
            {intl.formatMessage(
                {
                    id: 'users.uploadUsers',
                    defaultMessage: 'Upload users',
                }
            )}
        </label>
        <input type={"file"} ref={fileInput} id={"contact-upload"}
               className={"hidden-element"} name={"contact-upload"}
               onChange={e => handleUserUpload(e)}
               accept={".xlsx, text/xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"}/>
        {
            uploadFiles.length > 0 &&
            <UploadPopup uploadFile={uploadFiles[0]}
                         emptyFileInput={()=>emptyFileInput()}
                         reTriggerFileInput={()=>reTriggerFileInput()}
                         setNewUploadFiles={files=>setUploadFiles(files)}
                         refetchUsers={()=>refetchUsers()}
            />
        }
    </>
}

function UploadPopup({uploadFile, emptyFileInput, reTriggerFileInput, setNewUploadFiles, refetchUsers}) {
    const intl = useIntl()

    const [fileToValidate, setFileToValidate] = useState(uploadFile)
    const [validateData, setValidateData] = useState({})

    const fileValidated = validateData && validateData.hasOwnProperty("status") && validateData.status === "OK"
    const fileHasErrors = validateData && validateData.hasOwnProperty("status") && validateData.status !== "OK"

    const getSMSCountryCode = GetSMSCountryCode()
    const postUploadValidate = PostUserUploadValidate()
    const postUploadSave = PostUserUploadSave()

    const loading = postUploadValidate.isLoading || getSMSCountryCode.isLoading
    const error = postUploadValidate.isError || getSMSCountryCode.isError
    const success = postUploadValidate.isSuccess && getSMSCountryCode.isSuccess
    const error400 = postUploadValidate.isError && postUploadValidate.error.response.status === 400

    const [showUserTable, setShowUserTable] = useState(false)
    const [userTableData, setUserTableData] = useState([])

    const checkFile = () => {
        let uploadFileData = new FormData()
        uploadFileData.append("fileup", fileToValidate, fileToValidate.name)

        postUploadValidate.mutate(uploadFileData, {
            onError: error => {
                console.log(error)
            },
            onSuccess:data => {
                setValidateData(data.data)
            }
        })
    }

    useEffect(() => {
        setFileToValidate(uploadFile)
    },[uploadFile])

    useEffect(() => {
        checkFile()
    },[fileToValidate])

    const {setSnackbarData} = useSnackbar()

    const addUsers = () => {
        if (fileValidated) {
            const saveData = validateData.annieusers

            postUploadSave.mutate(saveData, {
                onError: error => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'contact.upload.saveFailed',
                                defaultMessage: 'Save failed. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                    console.log(error)
                },
                onSuccess:data => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'users.upload.saveSuccess',
                                defaultMessage: 'Users added.',
                            }),
                        state: "success",
                        autoHide: true,
                    })
                    refetchUsers()
                    emptyFileInput()
                }
            })
        } else {
            alert("Data not validated")
        }
    }
    if (uploadFile) {
        return <Popup closePopup={()=>emptyFileInput()} className={"full-width"}>
            <FileDisplayAndUpload currentFile={uploadFile.name} reTriggerFileInput={()=>reTriggerFileInput()} setNewUploadFiles={files=>setNewUploadFiles(files)} validating={loading}/>
            <div className={"upload-results"}>
                {loading &&
                <>
                    <h2>{intl.formatMessage(
                        {
                            id: 'contact.upload.checkingFile',
                            defaultMessage: 'Checking file...',
                        })}</h2>
                    <div className={"slat-container"}>
                        <FileCheckSkeleton />
                    </div>
                </>
                }
                {
                    error &&
                    <>
                        <h2 className={"error"}>
                            {intl.formatMessage(
                                {
                                    id: 'contact.upload.fileCheckFailed',
                                    defaultMessage: 'File check failed. Please try again.',
                                })}
                        </h2>
                        {error400 &&
                        <>
                            <p className={"error-description"}>
                                <FormattedMessage
                                    id={"users.upload.fileCheck400"}
                                    defaultMessage={"Make sure that the uploaded .xlsx-file resembles the provided template.{br}NB: the data tab has to be named annieuser"}
                                    values={{br: <br/>}}/>
                            </p>
                            <a className={"download-template"} href={"/admin/downloads/annieuser.xlsx"} download={true}>{intl.formatMessage({
                                id:'contact.downloadTemplate',
                                defaultMessage:"Download template"
                            })}</a>
                        </>
                        }
                    </>
                }
                {
                    success &&
                    <>
                        {showUserTable ?
                            <>
                                <StudentTable tableData={userTableData} hideStudentTable={()=>setShowUserTable(false)} supportRolesData={[]}/>
                            </> :
                            <>
                                <FileInfo data={_.cloneDeep(validateData)} setShowStudentTable={()=>setShowUserTable(true)} setStudentTableData={(data)=>setUserTableData(data)} userInfo={true}/>
                                <FileErrorCheck data={validateData} countryCode={getSMSCountryCode.data[0].value}/>
                            </>
                        }
                    </>

                }
            </div>
            <div className={"options"}>
                {!loading && fileHasErrors &&
                <p className={"alert red"}>
                    <Alert />
                    {intl.formatMessage(
                        {
                            id: 'users.upload.fixErrors',
                            defaultMessage: 'Fix errors before adding users',
                        })}
                </p>
                }
                {postUploadSave.isLoading &&
                <p className={"loading"}>
                    <LoadingIcon />
                    {intl.formatMessage(
                        {
                            id: 'users.upload.saving',
                            defaultMessage: 'Adding users...',
                        })}
                </p>
                }
                <button className={"cancel"} onClick={()=>emptyFileInput()}>
                    {intl.formatMessage({
                        id: 'modal.cancel',
                        defaultMessage: 'Cancel'})}
                </button>
                {loading || !fileValidated || postUploadSave.isLoading ?
                    <button className={"confirm"} disabled>
                        {intl.formatMessage({
                            id: 'users.addUsers',
                            defaultMessage: 'Add Users'
                        })}
                    </button> :
                    <button className={"confirm"} onClick={() => addUsers()}>
                        {intl.formatMessage({
                            id: 'users.addUsers',
                            defaultMessage: 'Add users'
                        })}
                    </button>
                }
            </div>
        </Popup>
    }
    return null
}

function FileErrorCheck({data, countryCode}){
    const intl = useIntl()
    const noErrors = data?.errors.length < 1
    const noUserId = !noErrors && data.errors.some(e => e.error.includes("no userid"))
    const noFirstname = !noErrors && data.errors.some(e => e.error.includes("no firstname"))
    const noLastname = !noErrors && data.errors.some(e => e.error.includes("no lastname"))
    const noEmail = !noErrors && data.errors.some(e => e.error.includes("no email"))
    const tableData = _.cloneDeep(data)
    tableData?.annieusers.map(user => {
        if (user.hasOwnProperty("data") && user?.data.hasOwnProperty("meta")) {
            const metaKeys = Object.keys(user.data.meta)
            for (let i = 0; i < metaKeys.length; i++) {
                const key = user.data.meta[metaKeys[i]]
                if (!user.data.hasOwnProperty(metaKeys[i])) {
                    user.data[metaKeys[i]] = key
                }
            }
            delete user.data.meta
            delete user.data.id
            const superuser = user.data?.superuser ? "true" : "false"
            const notifications = user.data?.notifications
            const validuntil = user.data?.validuntil ? user.data.validuntil : "-"
            delete user.data.superuser
            delete user.data.notifications
            delete user.data.validuntil
            user.data.superuser = superuser
            user.data.notifications = notifications
            user.data.validuntil = validuntil
        }
        return user
    })
    tableData?.errors.map(user => {
        if (user.hasOwnProperty("data") && user?.data.hasOwnProperty("meta")) {
            const metaKeys = Object.keys(user.data.meta)
            for (let i = 0; i < metaKeys.length; i++) {
                const key = user.data.meta[metaKeys[i]]
                if (!user.data.hasOwnProperty(metaKeys[i])) {
                    user.data[metaKeys[i]] = key
                }
            }
            delete user.data.meta
            delete user.data.id
            const superuser = user.data?.superuser ? "true" : "false"
            const notifications = user.data?.notifications
            const validuntil = user.data?.validuntil ? user.data.validuntil : "-"
            delete user.data.superuser
            delete user.data.notifications
            delete user.data.validuntil
            user.data.superuser = superuser
            user.data.notifications = notifications
            user.data.validuntil = validuntil
        }
        return user
    })

    const allUsers = tableData?.annieusers.concat(tableData.errors)
    const contactsWithNoPhonenumber = allUsers.filter(e => !e.data.hasOwnProperty("phonenumber") || !e?.data?.phonenumber || e?.data?.phonenumber.length < 1)
    const noPhonenumber = contactsWithNoPhonenumber.length > 0

    const contactsWithDifferentCountryCode = allUsers.filter(e => e.data.hasOwnProperty("phonenumber") && e.data["phonenumber"] && !e.data["phonenumber"].startsWith(countryCode))

    const numberIsInvalid = e => {
        const phoneNumber = e.data.hasOwnProperty("phonenumber") && e.data["phonenumber"]
        if (!phoneNumber) {
            return false
        }

        let zeroAfterCountryCode

        if (phoneNumber.startsWith(countryCode)) {
            const numberWithoutCountryCode = phoneNumber.substring(countryCode.length)
            zeroAfterCountryCode = numberWithoutCountryCode.startsWith("0")
        }

        const phoneNumberLength = phoneNumber.length
        const invalidLength = phoneNumberLength < 5 || phoneNumberLength > 15

        const charsOutsideValid = /[^+,0-9]/g.test(phoneNumber)

        const multiplePlusSigns = phoneNumber.match(/[+]/g) && phoneNumber.match(/[+]/g).length > 1

        return zeroAfterCountryCode || invalidLength || charsOutsideValid || multiplePlusSigns
    }

    const numbersFailedValidation = allUsers.some(e => numberIsInvalid(e))

    const contactsThatFailedNumberValidation = allUsers.filter(e => numberIsInvalid(e))

    return <div className={"scroll-content"}>
        <div className={"survey-summary-container"}>
            <Slat header={intl.formatMessage(
                {
                    id: 'user.upload.errorCheck.header.noUserId',
                    defaultMessage: 'User ID',
                })}
                  text={!noUserId && intl.formatMessage(
                      {
                          id: 'user.upload.errorCheck.text.noUserId',
                          defaultMessage: 'All users have IDs',
                      })}
                  success={!noUserId}
                  errorText={noUserId && intl.formatMessage(
                      {
                              id: 'user.upload.errorCheck.noUserId',
                              defaultMessage: 'Missing userid(s)',
                      })}
                  expandable={noUserId}
            >
                {noUserId &&
                <DataTable data={tableData} error={"no userid"} userTable={true} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'user.upload.errorCheck.header.email',
                    defaultMessage: 'Email',
                })}
                  text={!noUserId && intl.formatMessage(
                      {
                          id: 'user.upload.errorCheck.text.noEmail',
                          defaultMessage: 'All users have emails',
                      })}
                  success={!noUserId}
                  errorText={noUserId && intl.formatMessage(
                      {
                          id: 'user.upload.errorCheck.noEmail',
                          defaultMessage: 'Missing email(s)',
                      })}
                  expandable={noUserId}
            >
                {noEmail &&
                <DataTable data={tableData} error={"no email"} userTable={true} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.firstname',
                    defaultMessage: 'Firstname',
                })}
                  text={!noFirstname ? intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.firstname',
                          defaultMessage: 'No missing fistnames',
                      }) : false}
                  success={!noFirstname}
                  errorText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.firstname',
                          defaultMessage: 'Missing firstname(s)',
                      })}
                  expandable={noFirstname}
            >
                {noFirstname &&
                <DataTable data={tableData} error={"no firstname"} userTable={true} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.lastname',
                    defaultMessage: 'Lastname',
                })}
                  text={!noLastname ? intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.lastname',
                          defaultMessage: 'No missing lastnames',
                      }) : false}
                  success={!noLastname}
                  errorText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.lastname',
                          defaultMessage: 'Missing lastname(s)',
                      })}
                  expandable={noLastname}
            >
                {noLastname &&
                <DataTable data={tableData} error={"no lastname"} userTable={true} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.missingPhonenumbers',
                    defaultMessage: 'Phonenumbers',
                })}
                  text={!noPhonenumber && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.missingPhonenumbers',
                          defaultMessage: 'No missing phonenumbers',
                      })}
                  success={true}
                  info={noPhonenumber}
                  infoText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.missingPhonenumbers',
                          defaultMessage: 'Missing phonenumber(s)',
                      })}
                  expandable={noPhonenumber}
            >
                {noPhonenumber &&
                <DataTable data={tableData} error={false} dataToDisplay={contactsWithNoPhonenumber} userTable={true} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.foreignNumbers',
                    defaultMessage: 'Foreign numbers',
                })}
                  success={true}
                  text={contactsWithDifferentCountryCode.length < 1 && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.foreignNumbers',
                          defaultMessage: 'No foreign phonenumbers',
                      })}
                  info={contactsWithDifferentCountryCode.length > 0}
                  infoText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.foreignNumbers',
                          defaultMessage: 'Foreign number(s)',
                      })}
                  expandable={contactsWithDifferentCountryCode.length > 0}
            >
                {contactsWithDifferentCountryCode.length > 0 &&
                <DataTable data={tableData} error={false} dataToDisplay={contactsWithDifferentCountryCode} userTable={true} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.validNumbers',
                    defaultMessage: 'Valid numbers',
                })}
                  success={true}
                  text={!numbersFailedValidation && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.validNumbers',
                          defaultMessage: 'Valid numbers',
                      })}
                  info={numbersFailedValidation}
                  infoText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.validNumbers',
                          defaultMessage: 'Some numbers failed validation. Please check numbers.',
                      })}
                  expandable={numbersFailedValidation}
            >
                {numbersFailedValidation &&
                <DataTable data={tableData} error={false} dataToDisplay={contactsThatFailedNumberValidation} userTable={true}/>
                }
            </Slat>
        </div>
    </div>
}

function UsersRender({getUsers, getUserMeta, getSupportRoles}) {
    const intl = useIntl()

    return <div className="contacts-table-container">
        {getUsers.isLoading || getUserMeta.isLoading || getSupportRoles.isLoading &&
        <h2>{intl.formatMessage(
            {
                id: 'loading',
                defaultMessage: 'Loading...',
            })}</h2>
        }
        {getUsers.isError || getUserMeta.isError || getSupportRoles.isError &&
        <h2>{intl.formatMessage(
            {
                id: 'contact.errorGettingUsers',
                defaultMessage: 'Error in getting users.',
            })}</h2>
        }
        {getUsers.isSuccess && getUserMeta.isSuccess && getSupportRoles.isSuccess &&
        <UsersTable tableData={getUsers.data} userMeta={getUserMeta?.data[0]} roleOptions={Object.keys(getSupportRoles?.data[0])} refetchUsers={()=>getUsers.refetch()} getUsers={getUsers}/>
        }
    </div>
}

function UsersTable({tableData, refetchUsers, userMeta, roleOptions}) {
    const intl = useIntl()
    const formatDataToTable = data => {
        return _.cloneDeep(data).map(user => {
            if (user.hasOwnProperty("meta") && user?.meta) {
                const metaKeys = Object.keys(user.meta)
                for (let i = 0; i < metaKeys.length; i++) {
                    const key = user.meta[metaKeys[i]]
                    if (!user.hasOwnProperty(metaKeys[i])) {
                        user[metaKeys[i]] = key
                    }
                }
                delete user.meta
            }
            delete user.updated
            delete user.updatedby
            delete user.created
            delete user.createdby
            delete user.iv
            return user
        })
    }

    const userData = useMemo( ()=> formatDataToTable(tableData), [tableData])

    const userHeaders = Object.keys(userMeta).sort((a, b) => {
        return userMeta[a]?.order - userMeta[b]?.order
    })

    const headerName = (column) => {
        return intl.formatMessage({
            id: column,
            defaultMessage: column
        })
    }

    const columnArray = userHeaders.map( header => {
        const toString = header === 'superuser' || header === 'validuntil'
        const nonEditable = header === 'id'
        const dropdown = header === 'superuser' || header === 'notifications'
        const list = header === 'aliases'

        let cellObj = {
            Header: headerName(header),
            accessor: header,
            width: 200,
            minWidth: 50,
            Cell: ({cell: {value}, row, column}) => (
                <CellEdit value={value} header={header} row={row} column={column} toString={toString} tagInput={list}/>
            )
        }
        if (nonEditable) {
            cellObj["Cell"] = ({cell: {value}}) => (
                <span className={"ellipsis"}>{value}</span>
            )
        }
        if (toString) {
            cellObj["id"] = header
            cellObj["accessor"] = e => e[header]?.toString()
        }
        if (dropdown) {
            cellObj["width"] = 150
            cellObj["Cell"] = ({cell: {value}, row, column}) => (
                <CellDropdown value={value} header={header} row={row} column={column} toString={toString}/>
            )
        }

        return cellObj
    })

    const [data, setData] = useState(userData)
    const [originalData, setOriginalData] = useState(data)
    const columns = useMemo(()=> columnArray, [columnArray])
    const [skipPageReset, setSkipPageReset] = useState(false)
    const [dataIsEdited, setDataIsEdited] = useState(false)

    useEffect(()=>{
        setOriginalData(userData)
        setData(userData)
    },[userData])

    const updateUserData = (rowIndex, columnId, value) => {
        setSkipPageReset(true)
        setData(old =>
            old.map((row, index) => {
                if (index === rowIndex) {
                    return {
                        ...old[rowIndex],
                        [columnId]: value,
                    }
                }
                return row
            })
        )
    }

    const CellDropdown = ({value: initialValue, row: { index }, column: { id }, header, toString}) => {
        const [value, setValue] = useState(initialValue)
        const [tempValue, setTempValue] = useState(initialValue)
        const [isDropdownOpen, setIsDropdownOpen] = useState(false)
        const dropdownRef = useRef(null)
        const originalValue = toString ? JSON.stringify(originalData[index][header]) : originalData[index][header]
        const edited = originalValue !== value

        useEffect(() => {
            if (initialValue !== value) {
                updateUserData(index, id, value)
            }
        }, [value])

        useEffect(() => {
            setValue(initialValue)
        }, [initialValue])

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsDropdownOpen(false)
                    setTempValue(value)
                }
            }
            document.addEventListener("mousedown", handleClickOutside)
            return () => {
                document.removeEventListener("mousedown", handleClickOutside)
            }
        }, [])

        const onChange = e => {
            setValue(e.target.value)
        }

        const onNotificationChange = (selectedValue) => {
            let newValues = [...tempValue.split(',')]

            if (selectedValue === 'DISABLED') {
                newValues = ['DISABLED'];
            } else {
                if (newValues.includes(selectedValue)) {
                    // Unselect the option
                    newValues = newValues.filter(v => v !== selectedValue)
                } else {
                    // Add new option, remove DISABLED if selected
                    newValues = newValues.filter(v => v !== 'DISABLED')
                    newValues.push(selectedValue)
                }
            }

            setTempValue(newValues.join(','))
        }

        const handleConfirm = () => {
            // Set final value and close dropdown
            const finalValue = tempValue.trim() === '' ? 'DISABLED' : tempValue
            setValue(finalValue)
            setTempValue(finalValue)
            setIsDropdownOpen(false)
        }

        let options = ["placeholder","missing options"]
        if (header === "superuser") {
            options = ["true", "false"]
        }
        if (header === "notifications") {
            options = ["IMMEDIATE", "STATS"]
        }

        if (header === "notifications") {
            return (
                <div ref={dropdownRef} className={`multiselect cell-dropdown select ${edited ? "edited" : ""}`}>
                    <div
                        className="multiselect-label"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        {value
                            ? value.split(',').map((v) => intl.formatMessage({ id: `notifications.${v}`, defaultMessage: v })).join(', ')
                            : intl.formatMessage({id:'selectNotifications', defaultMessage:'Select Notifications'})}
                    </div>
                    {isDropdownOpen && (
                        <div className="multiselect-options">
                            {options.map((option, i) => (
                                <label key={i}>
                                    <input
                                        type="checkbox"
                                        value={option}
                                        checked={tempValue.split(',').includes(option)}
                                        onChange={() => onNotificationChange(option)}
                                    />
                                    {intl.formatMessage({ id: `notifications.${option}`, defaultMessage:option })}
                                </label>
                            ))}
                            <button
                                className="confirm"
                                onClick={handleConfirm}
                            >
                                {intl.formatMessage({id:"select", defaultMessage:"Select"})}
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        return <select value={value ? value : ""} className={edited ? "cell-dropdown edited" : "cell-dropdown"} onChange={onChange}>
            {options.map((e,i) => {
                return <option value={e} key={i}>{e}</option>
            })}
        </select>
    }

    const CellEdit = ({value: initialValue, row: { index }, column: { id }, header, toString, tagInput}) => {
        const [value, setValue] = useState(initialValue)
        const originalValue = toString ? JSON.stringify(originalData[index][header]) : originalData[index][header]
        const edited = originalValue !== value

        const [textareaContentRows, setTextareaContentRows] = useState(1)
        const setTextareaHeight = e => {
            const lineHeight = 13.8
            const minRows = 1
            const maxRows = 20
            const previousRows = e.rows
            const padding = 10
            e.rows = minRows // reset rows for decreasing textarea
            const currentRows = Math.round((e.scrollHeight - padding) / lineHeight)
            if (currentRows === previousRows) {
                e.rows = currentRows
            }
            if (currentRows >= maxRows) {
                e.rows = maxRows
                e.scrollTop = e.scrollHeight
            }
            setTextareaContentRows(currentRows < maxRows ? currentRows : maxRows)
        }

        const textareaRef = useRef(null)

        useEffect(()=>{
            if (!tagInput) {
                setTextareaHeight(textareaRef?.current)
            }
        },[])

        const onChange = e => {
            setValue(e.target.value)
            setTextareaHeight(e.target)
        }

        const onBlur = () => {
            if (initialValue !== value) {
                updateUserData(index, id, value)
            }
        }

        const tagOnChange = value => {
            setValue(value)
            // Do not update value if initialValue undefined and value is an empty array
            if ((initialValue !== value) && (value.length || initialValue?.length)) {
                updateUserData(index, id, value)
            }
        }

        useEffect(() => {
            setValue(initialValue)
        }, [initialValue])

        if (tagInput) {
            return <TagInput onChange={(value)=>tagOnChange(value)} returnArray={true} initialValue={value}/>
        }
        return <textarea ref={textareaRef} rows={textareaContentRows} value={value ? value : ""} className={edited ? "editable-cell edited" : "editable-cell"} onChange={onChange} onBlur={onBlur}/>
    }

    useEffect(() => {
        setSkipPageReset(false)
        setDataIsEdited(JSON.stringify(data) !== JSON.stringify(originalData))
    },[data])

    const annieUserPost = PostAnnieUser()
    const [usersToPost, setUsersToPost] = useState([])
    const {setSnackbarData} = useSnackbar()

    const resetData = () => {
        setData(originalData)
        setUsersToPost([])
    }

    const saveUserChanges = () => {
        let usersArray = []
        for (let i = 0; i < data.length; i++) {
            if (JSON.stringify(data[i]) !== JSON.stringify(originalData[i])) {
                const userData = formatUserDataForPost(data[i])
                usersArray.push(userData)
            }
        }
        //console.log(usersArray)
        setUsersToPost(usersArray)
    }

    useEffect(()=>{
        if (usersToPost.length > 0) {
            annieUserPost.mutate(usersToPost, {
                onError: error => {
                    console.log(error)
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.userPostFailed',
                                defaultMessage: 'Error updating user data. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                },
                onSuccess:() => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.userPostSuccess',
                                defaultMessage: 'User data updated!',
                            }),
                        state: "success",
                        autoHide: true,
                    })
                    setUsersToPost([])
                    refetchUsers()
                }
            })
        }
    },[usersToPost])

    return <div className={"contacts-table"}>
        <Table columns={columns} data={data} userHeaders={userHeaders} userMeta={userMeta} roleOptions={roleOptions} updateUserData={updateUserData} skipPageReset={skipPageReset} resetData={resetData} dataIsEdited={dataIsEdited} refetchUsers={refetchUsers} userData={userData} saveUserChanges={()=>saveUserChanges()} annieUserPost={annieUserPost} setSnackbarData={setSnackbarData} headerName={headerName}/>
    </div>
}

function Table({columns, data, userHeaders, userMeta, roleOptions, updateUserData, skipPageReset, resetData, dataIsEdited, refetchUsers, saveUserChanges, annieUserPost, setSnackbarData, headerName}) {
    const intl = useIntl()
    const postLoading = annieUserPost.isLoading
    const [userToDelete, setUserToDelete] = useState({})
    const [showDeletePopup, setShowDeletePopup] = useState(false)

    const updateAnnieUserData = data => {
        annieUserPost.mutate(data, {
            onError: error => {
                console.log(error)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'toast.userPostFailed',
                            defaultMessage: 'Error updating user data. Please try again.',
                        }),
                    state: "error",
                    autoHide: true,
                })
            },
            onSuccess:() => {
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'toast.userPostSuccess',
                            defaultMessage: 'User data updated!',
                        }),
                    state: "success",
                    autoHide: true,
                })
                if (showDeletePopup) {
                    setUserToDelete({})
                    setShowDeletePopup(false)
                }
                refetchUsers()
            }
        })
    }

    function DefaultColumnFilter({column: { filterValue, setFilter }}) {
        return (
            <input
                type={"search"}
                onClick={e => e.stopPropagation()}
                value={filterValue || ''}
                onChange={e => {setFilter(e.target.value || undefined)}}
                placeholder={'Search'}
            />
        )
    }

    const EditableCell = ({
            value: initialValue,
            row: { index },
            column: { id },
            updateUserData,
        }) => {
        const [value, setValue] = useState(initialValue)
        const [originalValue] = useState(value)
        const [edited, setEdited] = useState(false)

        const onChange = e => {
            setValue(e.target.value)
        }

        const onBlur = () => {
            if (initialValue !== value) {
                updateUserData(index, id, value)
            }
            setEdited(originalValue !== value)
        }

        useEffect(() => {
            setValue(initialValue)
            if (originalValue === initialValue) {
                setEdited(false)
            }
        }, [initialValue])

        return <input value={value || ""} className={edited ? "editable-cell edited" : "editable-cell"} onChange={onChange} onBlur={onBlur}/>
    }

    const defaultColumn = React.useMemo(() => ({
        Filter: DefaultColumnFilter,
        Cell: EditableCell,
    }), [])

    const RowOptions = ({row}) => {
        return <><span className={"non-editable"}>
            <div className={"popover-toggle"} onClick={()=> {
                setUserToDelete(row.original)
                setShowDeletePopup(true)
            }}>
            <OptionsIcon />
        </div>
        </span>
        </>
    }

    function deleteUser(user) {
        const userData = formatUserDataForPost(user)
        const date = validUntilDate(new Date())
        userData.validuntil = date
        updateAnnieUserData([userData])
    }

    const pageSizeFromStorage = localStorage.getItem("pageSize") ? Number(localStorage.getItem("pageSize")) : 50
    const hiddenColumnsFromStorage = localStorage.getItem("hiddenUserColumns") ? JSON.parse(localStorage.getItem("hiddenUserColumns")) : []
    const columnOrderFromStorage = localStorage.getItem("userColumnOrder") ? JSON.parse(localStorage.getItem("userColumnOrder")) : []
    const filtersFromStorage = sessionStorage.getItem("userTableFilters") ? JSON.parse(sessionStorage.getItem("userTableFilters")) : []
    const sortByFromStorage = sessionStorage.getItem('userTableSortBy') ? JSON.parse(sessionStorage.getItem('userTableSortBy')) : [];

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        prepareRow,
        preGlobalFilteredRows,
        setGlobalFilter,
        state,
        state: {pageIndex, pageSize, filters, sortBy},
        setHiddenColumns,
        setColumnOrder,
        setAllFilters
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            autoResetPage: !skipPageReset,
            updateUserData,
            initialState: {
                pageIndex: 0,
                pageSize: pageSizeFromStorage,
                hiddenColumns: hiddenColumnsFromStorage,
                columnOrder: columnOrderFromStorage,
                filters: filtersFromStorage,
                sortBy: sortByFromStorage
            }
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        //useFlexLayout,
        usePagination,
        useRowSelect,
        useColumnOrder,
        useResizeColumns,
        hooks => {
            hooks.visibleColumns.push(columns => [
                {
                    id: 'options',
                    width: 30,
                    Header: () => null,
                    Cell: ({row}) => (
                        <RowOptions row={row} />
                    ),
                },
                ...columns,
            ])
        }
    )

    const hideColumns = hiddenColumns => {
        setHiddenColumns(hiddenColumns)
        localStorage.setItem("hiddenUserColumns", JSON.stringify(hiddenColumns))
    }

    const orderColumns = columnOrder => {
        setColumnOrder(columnOrder)
        localStorage.setItem("userColumnOrder", JSON.stringify(columnOrder))
    }

    useEffect(()=>{
        sessionStorage.setItem("userTableFilters", JSON.stringify(filters))
    },[filters])

    useEffect(() => {
        sessionStorage.setItem('userTableSortBy', JSON.stringify(sortBy))
    }, [sortBy])

    const tableRef = useRef(null)
    const [tableWidth, setTableWidth] = useState(null)

    useEffect(()=>{
        if (tableRef?.current) {
            setTableWidth(tableRef.current.offsetWidth)
        }
    },[tableRef?.current?.offsetWidth])

    return <><div className={"contact-table-container"} ref={tableRef}>
        <div className={"toolbar-container"}>
            <div className={"filters"}>
                <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    students={rows.length}
                    usersSearch={true}
                />
                <FiltersDisplay filters={filters} removeFilters={()=>setAllFilters([])}/>
            </div>
            <div className={"buttons"}>
                <AddUser options={userHeaders} userMeta={userMeta} optionOrder={columnOrderFromStorage} headerName={headerName} refetchUsers={refetchUsers} roleOptions={roleOptions}/>
                <ColumnEditor contactHeaders={userHeaders}
                              hideColumns={hiddenColumns=>hideColumns(hiddenColumns)}
                              orderColumns={columnOrder=>orderColumns(columnOrder)}
                              userColumns={true}
                />
            </div>
        </div>
        <div className={"table-container"}>
            <table {...getTableProps()} className={`contact-table user-table ${rows.length < 1 ? "no-results-table" : ""}`}>
                <thead>
                {
                    headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th {...column.getHeaderProps()}>
                                    <span {...column.getSortByToggleProps({title:undefined})}>
                                        {column.render('Header')}
                                    </span>
                                    {column.isSorted &&
                                    <span>
                                    {column.isSortedDesc ?
                                        ' 🔽' :
                                        ' 🔼'
                                    }
                                </span>
                                    }
                                    {column.canFilter && <div className={column.filterValue ? "filter-active filter-container": "filter-container"}>{column.render('Filter')}</div>}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {
                    page.map(row => {
                        prepareRow(row)
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    return <td title={cell.value} {...cell.getCellProps()}>
                                        {cell.render('Cell')}
                                    </td>
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            {rows.length < 1 &&
                <div className={"no-results"}>{intl.formatMessage({id:'noResults', defaultMessage:'No results with given search term'})}</div>
            }
        </div>
        <div className="pagination">
            <select
                value={pageSize}
                onChange={e => {
                    setPageSize(Number(e.target.value))
                    localStorage.setItem("pageSize", e.target.value)
                }}
            >
                {[10, 50, 100, 200, 500].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                        {intl.formatMessage(
                            {
                                id: 'surveys.recipients.table.show',
                                defaultMessage: 'Show',
                            })}{' '}
                        {pageSize}
                    </option>
                ))}
            </select>
            <div className={"page-index"}>
                {intl.formatMessage(
                    {
                        id: 'surveys.recipients.table.page',
                        defaultMessage: 'Page',
                    })}{' '}
                <strong>
                    {pageIndex + 1}{' '}
                    {intl.formatMessage(
                        {
                            id: 'surveys.recipients.table.of',
                            defaultMessage: 'of',
                        })}
                    {' '}{pageOptions.length}
                </strong>
            </div>
            <div className={"buttons"}>
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    <First/>
                </button>{' '}
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                    <Previous/>
                </button>{' '}
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                    <Next />
                </button>{' '}
                <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                    <Last/>
                </button>
            </div>
        </div>
        {dataIsEdited &&
        <div className={"fixed-to-bottom"} style={{width:tableWidth}}>
            <button className={"cancel remove-filters"} onClick={()=>resetData()}>
                {intl.formatMessage({
                    id: 'cancelChanges',
                    defaultMessage: 'Cancel changes'})}
            </button>
            {annieUserPost.isLoading ?
                <button className={"main-button loading"} disabled>
                    <LoaderIcon/>
                    {intl.formatMessage({
                        id: 'saving',
                        defaultMessage: 'Saving...'})}
                </button> :
                <button className={"main-button"} onClick={()=>saveUserChanges()}>
                    <SaveIcon />
                    {intl.formatMessage({
                        id: 'survey.unsavedChanges.confirm',
                        defaultMessage: 'Save changes'})}
                </button>
            }
        </div>
        }
    </div>
        {showDeletePopup &&
        <Popup closePopup={() => {
            setShowDeletePopup(false)
            setUserToDelete({})
        }}>
            <h1>
                {intl.formatMessage({
                    id: 'users.delete.user',
                    defaultMessage: 'Delete user',
                })}
                <b> {userToDelete?.userid || userToDelete?.id}</b>
            </h1>
            <p>
                {intl.formatMessage({
                    id: 'users.delete.user.text',
                    defaultMessage: 'Are you sure you want to delete this user?',
                })}
            </p>
            <div className={"modal-options"}>
                <button className={"cancel"} onClick={()=> {
                    setShowDeletePopup(false)
                    setUserToDelete({})
                }}>
                    {intl.formatMessage({
                        id: 'modal.cancel',
                        defaultMessage: 'Cancel',
                    })}
                </button>
                {postLoading ?
                    <button disabled className={"confirm alert"}>
                        {intl.formatMessage({
                            id: 'deleting',
                            defaultMessage: 'Deleting...',
                        })}
                    </button> :
                    <button className={"confirm alert"} onClick={()=>deleteUser(userToDelete)}>
                        {intl.formatMessage({
                            id: 'survey.delete',
                            defaultMessage: 'Delete',
                        })}
                    </button>
                }
            </div>
        </Popup>
        }
        </>
}

export function AddUser({options, optionOrder, headerName, refetchUsers, userMeta, roleOptions}) {
    const intl = useIntl()
    const [showPopup, setShowPopup] = useState(false)
    const [loading, setLoading] = useState(false)
    const {instanceLang} = useLanguageData()
    const sortedOptions = options.sort((a, b) => {
        const requiredA =  userMeta[a].required
        const requiredB = userMeta[b].required
        const orderA = userMeta[a].order
        const orderB = userMeta[b].order

        if (requiredA && !requiredB) {
            return -1
        }
        if (!requiredA && requiredB) {
            return 1
        }

        return orderA - orderB
    })
    const annieUserPost = PostAnnieUser()
    const [annieUserToPost, setAnnieUserToPost] = useState([])
    const {setSnackbarData} = useSnackbar()
    const [userToPost, setUserToPost] = useState({})
    const requiredFields = Object.keys(userMeta).filter(key => userMeta[key].required === true)
    const dropDown = Object.keys(userMeta).filter(key => userMeta[key].type === "enum")
    const boolean = Object.keys(userMeta).filter(key => userMeta[key].type === "boolean")
    const list = Object.keys(userMeta).filter(key => userMeta[key].type === "list")

    const [roleTranslationNeeded, setRoleTranslationNeeded] = useState(false)
    const [selectedRole, setSelectedRole] = useState(null)
    const getRoleTranslation = GetRoleTranslationWithInstanceLang(selectedRole, instanceLang, selectedRole !== null)

    const onSubmit = e => {
        e.preventDefault()
        setLoading(true)
        if (userToPost.hasOwnProperty("role")) {
            setSelectedRole(userToPost.role)
            setRoleTranslationNeeded(true)
        } else {
            setAnnieUserToPost([formatUserDataForPost(userToPost, true, true)])
        }
    }

    useEffect(()=>{
        if (getRoleTranslation.isSuccess && roleTranslationNeeded) {
            const roleName = getRoleTranslation.data[0]?.value || selectedRole
            const updatedUserToPost = {
                ...userToPost,
                role: roleName
            }
            setAnnieUserToPost([formatUserDataForPost(updatedUserToPost, true, true)])

            setRoleTranslationNeeded(false)
        }
    },[getRoleTranslation.isSuccess, getRoleTranslation.data, userToPost, roleTranslationNeeded])

    const updateUserToPost = (option, value) => {
        let newUserToPost = {...userToPost}
        if (!value || value?.length < 1) {
            delete newUserToPost[option]
        } else {
            newUserToPost[option] = value
        }
        setUserToPost(newUserToPost)
    }

    useEffect(()=>{
        if (annieUserToPost.length > 0) {
            annieUserPost.mutate(annieUserToPost, {
                onError: error => {
                    console.log(error)
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.userCreateFailed',
                                defaultMessage: 'Error in creating user. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                    setLoading(false)
                },
                onSuccess:() => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.userCreated',
                                defaultMessage: 'User created!',
                            }),
                        state: "success",
                        autoHide: true,
                    })
                    setAnnieUserToPost([])
                    setLoading(false)
                    setShowPopup(false)
                    refetchUsers()
                }
            })
        }
    },[annieUserToPost])

    return <>
        <button className={"main-button"} onClick={()=> setShowPopup(true)}>
            + {intl.formatMessage({
                id: 'users.addUser',
                defaultMessage: 'Add user',
            })}
        </button>
        {showPopup &&
        <Popup closePopup={()=>setShowPopup(false)} className={"add-user"}>
            <h1>
                {intl.formatMessage({
                    id: 'users.addUser',
                    defaultMessage: 'Add user',
                })}
            </h1>
            <form onSubmit={onSubmit}>
                <div className={"overflow-container"}>
                    {sortedOptions.map((e,i) => {
                        return <UserInput key={i}
                                          option={e}
                                          headerName={headerName}
                                          updateUserToPost={updateUserToPost}
                                          requiredFields={requiredFields}
                                          dropDown={dropDown}
                                          boolean={boolean}
                                          list={list}
                                          roleOptions={roleOptions}/>
                    })}
                </div>
                <div className={"modal-options options"}>
                    <button className={"cancel"} onClick={()=> {
                        setShowPopup(false)
                    }}>
                        {intl.formatMessage({
                            id: 'modal.cancel',
                            defaultMessage: 'Cancel',
                        })}
                    </button>
                    {loading ?
                        <input type={"submit"} className={"confirm add button"} value={intl.formatMessage({
                            id: 'users.addingUser',
                            defaultMessage: 'Creating user...',
                        })}/> :
                        <input type={"submit"} className={"confirm add button"} value={intl.formatMessage({
                            id: 'users.addUser',
                            defaultMessage: 'Add user',
                        })}/>
                    }
                </div>
            </form>
        </Popup>
        }
    </>
}

function UserInput({option, headerName, updateUserToPost, requiredFields, dropDown, boolean, roleOptions, list}) {
    const intl = useIntl()
    const hasDescription = ["validuntil"]

    let selectOptions = ["placeholder","missing options"]
    let optionPlaceholder = ""
    if (boolean.includes(option)) {
        selectOptions = ["true", "false"]
        optionPlaceholder = "false"
    }
    if (option === "notifications") {
        selectOptions = ["IMMEDIATE,STATS","IMMEDIATE", "STATS", "DISABLED"]
        optionPlaceholder = "IMMEDIATE,STATS"
    }
    if (option === "role") {
        selectOptions = ["NOTSET", ...roleOptions]
        optionPlaceholder = selectOptions[0]
    }
    const [value, setValue] = useState("")

    const showSelect = boolean.includes(option) || dropDown.includes(option)

    const onChange = e => {
        if (e === "NOTSET") {
            setValue("")
            updateUserToPost(option, "")
        }
        setValue(e.target.value)
        updateUserToPost(option, e.target.value)
    }

    const required = requiredFields.includes(option) && <span className={"required"} title={intl.formatMessage({
            id: 'required',
            defaultMessage: 'Required',
        })}>*</span>

    const description = hasDescription.includes(option) && <span className={"description"}>
        {option === "validuntil" && "YYYY-MM-DD"}
    </span>

    return <div>
        <label htmlFor={option+"-input"}>{headerName(option)}{required}{description}</label>
        {showSelect ?
            <select value={value ? value : optionPlaceholder} onChange={onChange}>
                {selectOptions.map((e,i) => {
                    return <option value={e} key={i}>
                        {option === "role" ?
                            intl.formatMessage({defaultMessage:e,id:'role.'+e}) :
                            intl.formatMessage({defaultMessage:e,id:e})
                        }
                    </option>
                })}
            </select> :
            list.includes(option) ?
                <TagInput onChange={(value)=>updateUserToPost(option, value)} returnArray={true}/> :
            <input required={requiredFields.includes(option)} type="text" value={value} className={"input"} id={option+"-input"} onChange={onChange} />
        }
    </div>
}